import axios from 'axios';
import authEvents from '../utils/authEvents';
import { cleanupAuthStorage, markSessionExpired } from '../utils/tokenValidator';
import { renewSession, isSessionRejection } from '../utils/sessionRenewal';

// Create axios instance with default config
const secureApi = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || '',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Single-flight refresh state for the response interceptor below.
// Mirrors utils/axiosConfig.js so the two refresh systems behave identically
// on disjoint axios instances (this one's interceptors only fire for secureApi
// callers; the global axios interceptors fire for raw-axios callers).
let isRefreshing = false;
let failedQueue = [];
const MAX_QUEUE_SIZE = 50;

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Coordinated logout via the documented event-driven pathway.
// AuthContext listens for LOGOUT_REQUIRED, clears state, and AppSecure
// navigates to /login when isAuthenticated flips. Storage cleanup is
// delegated to the canonical helper so the key list stays in one place.
const triggerLogout = () => {
  cleanupAuthStorage();
  markSessionExpired();
  authEvents.emitLogoutRequired();
};

// Request interceptor to add auth token
secureApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['x-auth-token'] = token;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
//
// On 401: silent refresh-retry first (single-flight + queue + cap of 50). Hard
// logout (clear localStorage + redirect to /login) only if the refresh itself
// fails. Pre-Guest-Requests-Hub V1, this was hard-logout-on-any-401. Ported
// from utils/axiosConfig.js:48 for parity with the global axios refresh path —
// see tasks/guest-requests-hub.md Phase 2.2.
//
// preserveBackendError flag (per-request opt-out): when set on the request
// config, the 4xx/5xx Arabic message rewrite is skipped so callers can read
// the backend's specific error.message. The 401 refresh-retry still runs.
secureApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (!error.response) {
      return Promise.reject(error);
    }

    // 401 refresh-retry path (runs regardless of preserveBackendError)
    if (error.response.status === 401 && !originalRequest._retry) {
      // Skip refresh for the auth endpoints themselves to prevent loops
      if (originalRequest.url?.includes('/login') || originalRequest.url?.includes('/refresh')) {
        return Promise.reject(error);
      }

      // If a refresh is already in flight, queue this request to replay after it
      if (isRefreshing) {
        if (failedQueue.length >= MAX_QUEUE_SIZE) {
          console.error('❌ secureApi refresh queue full, rejecting request');
          return Promise.reject(new Error('Too many concurrent refresh requests'));
        }

        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(token => {
            originalRequest.headers['x-auth-token'] = token;
            return secureApi(originalRequest);
          })
          .catch(err => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Shared, tab-safe renewal (utils/sessionRenewal.js). The pass this request carried is the
        // one known to be no good; a newer one another tab stored is used as is.
        const accessToken = await renewSession(originalRequest.headers?.['x-auth-token'] || null);

        // Replay original through the SAME instance so x-auth-token reattaches
        originalRequest.headers['x-auth-token'] = accessToken;

        processQueue(null, accessToken);
        isRefreshing = false;

        return secureApi(originalRequest);
      } catch (refreshError) {
        console.error('❌ secureApi token refresh failed:', refreshError);

        processQueue(refreshError, null);
        isRefreshing = false;

        // The session is over (refused, or nothing to renew with) — only then clear auth and bounce.
        // A network error or server fault keeps the session.
        if (isSessionRejection(refreshError)) {
          triggerLogout();
        }

        return Promise.reject(refreshError);
      }
    }

    // Backend-message-preserving callers opt out of the Arabic rewrite below.
    // 401 refresh-retry above already handled or fell through; from here on
    // we only do the message-rewrite policy.
    if (originalRequest?.preserveBackendError === true) {
      return Promise.reject(error);
    }

    switch (error.response.status) {
      case 401:
        // Reached only when _retry is true (refresh already failed) — keep
        // the existing legacy redirect for any caller that sees this path
        triggerLogout();
        break;

      case 403:
        // The store-review demo account (owner app step 6): say WHY nothing can be changed, rather than a
        // generic refusal that reads like a broken screen. Code kept so a client can translate it.
        if (error.response.data?.code === 'AUTH_013') {
          error.response.data = { message: error.response.data.error || error.response.data.message, code: 'AUTH_013' };
          break;
        }
        // Forbidden - user doesn't have permission
        // Don't expose details about what was forbidden
        error.response.data = {
          message: 'ليس لديك صلاحية للوصول إلى هذا المحتوى',
          code: 'ACCESS_DENIED'
        };
        break;

      case 404:
        // Not found - could be a security measure
        error.response.data = {
          message: 'المحتوى المطلوب غير موجود',
          code: 'NOT_FOUND'
        };
        break;

      case 500:
        // Server error - don't expose internal details
        console.error('🚫 500 Server Error');
        error.response.data = {
          message: 'حدث خطأ في الخادم',
          code: 'SERVER_ERROR'
        };
        break;

      default:
        console.error(`🚫 Error ${error.response.status}`);
        break;
    }

    return Promise.reject(error);
  }
);

// Secure API methods with built-in error handling
const secureApiService = {
  // Properties
  getProperties: async (params = {}) => {
    try {
      const response = await secureApi.get('/api/v1/properties', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching properties:', error);
      throw error;
    }
  },

  getProperty: async (propertyId) => {
    try {
      const response = await secureApi.get(`/api/v1/properties/${propertyId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching property ${propertyId}:`, error);
      throw error;
    }
  },

  createProperty: async (propertyData) => {
    try {
      const response = await secureApi.post('/api/v1/properties', propertyData);
      return response.data;
    } catch (error) {
      console.error('Error creating property:', error);
      throw error;
    }
  },

  updateProperty: async (propertyId, propertyData) => {
    try {
      // Backend handles smart routing based on:
      // 1. Property's Channex mapping (checks its own database)
      // 2. Which fields are being updated
      // Frontend just sends the changed fields - no routing logic needed here

      const response = await secureApi.put(`/api/v1/properties/${propertyId}`, propertyData);
      return response.data;
    } catch (error) {
      console.error(`Error updating property ${propertyId}:`, error);
      throw error;
    }
  },

  deleteProperty: async (propertyId) => {
    try {
      const response = await secureApi.delete(`/api/v1/properties/${propertyId}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting property ${propertyId}:`, error);
      throw error;
    }
  },

  // Channex-specific operations
  updateRoomType: async (propertyId, roomTypeId, roomTypeData) => {
    try {
      const response = await secureApi.put(`/api/v1/channex/room-type/${propertyId}/${roomTypeId}`, roomTypeData);
      return response.data;
    } catch (error) {
      console.error(`Error updating room type ${roomTypeId}:`, error);
      throw error;
    }
  },

  createRatePlan: async (propertyId, roomTypeId, ratePlanData) => {
    try {
      const response = await secureApi.post(`/api/v1/properties/${propertyId}/room-types/${roomTypeId}/rate-plans`, ratePlanData);
      return response.data;
    } catch (error) {
      console.error(`Error creating rate plan:`, error);
      throw error;
    }
  },

  updateRatePlan: async (propertyId, ratePlanId, ratePlanData) => {
    try {
      const response = await secureApi.put(`/api/v1/channex/rate-plan/${propertyId}/${ratePlanId}`, ratePlanData);
      return response.data;
    } catch (error) {
      console.error(`Error updating rate plan ${ratePlanId}:`, error);
      throw error;
    }
  },

  // Bookings
  getPropertyBookings: async (propertyId) => {
    try {
      const response = await secureApi.get(`/api/v1/properties/${propertyId}/bookings`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching bookings for property ${propertyId}:`, error);
      throw error;
    }
  },

  // Sync
  syncProperty: async (propertyId) => {
    try {
      const response = await secureApi.post(`/api/v1/sync/property/${propertyId}`);
      return response.data;
    } catch (error) {
      console.error(`Error syncing property ${propertyId}:`, error);
      throw error;
    }
  },

  bulkSync: async (propertyIds) => {
    try {
      const response = await secureApi.post('/api/v1/properties/bulk-sync', { propertyIds });
      return response.data;
    } catch (error) {
      console.error('Error bulk syncing properties:', error);
      throw error;
    }
  },

  getAgentTrace: async (params = {}) => {
    try {
      const response = await secureApi.get('/api/v1/agent-trace', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching agent trace:', error);
      throw error;
    }
  },

  // Channel detection and management
  getChannels: async (propertyId) => {
    try {
      const response = await secureApi.get(`/api/v1/channex/channels/${propertyId}`);
      return response.data;
    } catch (error) {
      console.error(`Error getting channels for property ${propertyId}:`, error);
      throw error;
    }
  },

  // Rate management
  getPropertyRates: async (propertyId) => {
    try {
      const response = await secureApi.get(`/api/v1/rates/property/${propertyId}`);
      return response.data;
    } catch (error) {
      console.error(`Error getting rates for property ${propertyId}:`, error);
      throw error;
    }
  },

  getEffectiveRate: async (propertyId, ratePlanId, date, channel = null) => {
    try {
      const params = channel ? `?channel=${channel}` : '';
      const response = await secureApi.get(`/api/v1/rates/property/${propertyId}/plan/${ratePlanId}/date/${date}${params}`);
      return response.data;
    } catch (error) {
      console.error(`Error getting effective rate for ${ratePlanId} on ${date}:`, error);
      throw error;
    }
  },

  getRateRange: async (propertyId, startDate, endDate, channel = null) => {
    try {
      const params = new URLSearchParams();
      params.append('startDate', startDate);
      params.append('endDate', endDate);
      if (channel) params.append('channel', channel);
      
      const response = await secureApi.get(`/api/v1/rates/property/${propertyId}/range?${params}`);
      return response.data;
    } catch (error) {
      console.error(`Error getting rate range for property ${propertyId}:`, error);
      throw error;
    }
  },

  updateChannelConstraints: async (propertyId, channelName, constraints) => {
    try {
      const response = await secureApi.put(`/api/v1/channex/channels/${propertyId}`, {
        channelName,
        constraints
      });
      return response.data;
    } catch (error) {
      console.error(`Error updating ${channelName} constraints:`, error);
      throw error;
    }
  },

  // Channex Onboarding API methods
  createPropertyInChannex: async (propertyId) => {
    try {
      const response = await secureApi.post(`/api/properties/${propertyId}/channex/create`);
      return response.data;
    } catch (error) {
      console.error(`Error creating property in Channex:`, error);
      throw error;
    }
  },

  getChannexStatus: async (propertyId) => {
    try {
      const response = await secureApi.get(`/api/properties/${propertyId}/channex/status`);
      return response.data;
    } catch (error) {
      console.error(`Error getting Channex status:`, error);
      throw error;
    }
  },

  connectChannel: async (propertyId, channelType) => {
    try {
      const response = await secureApi.post(`/api/properties/${propertyId}/connect-channel`, {
        channelType
      });
      return response.data;
    } catch (error) {
      console.error(`Error connecting ${channelType} channel:`, error);
      throw error;
    }
  },

  verifyChannel: async (propertyId, channelType) => {
    try {
      const response = await secureApi.post(`/api/properties/${propertyId}/verify-channel`, {
        channelType
      });
      return response.data;
    } catch (error) {
      console.error(`Error verifying ${channelType} channel:`, error);
      throw error;
    }
  },

  // Get room types for a Channex property (for multi-unit linking)
  getChannexRoomTypes: async (channexPropertyId) => {
    try {
      const response = await secureApi.get(`/api/v1/channex/properties/${channexPropertyId}/room-types`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching Channex room types:`, error);
      throw error;
    }
  },

  // Update linked room type for multi-unit properties
  updateLinkedRoomType: async (propertyId, linkedRoomTypeId) => {
    try {
      const response = await secureApi.put(`/api/properties/${propertyId}/linked-room-type`, {
        linkedRoomTypeId
      });
      return response.data;
    } catch (error) {
      console.error(`Error updating linked room type:`, error);
      throw error;
    }
  },

  // Step 2: Create room types in Channex
  createRoomTypesInChannex: async (propertyId) => {
    try {
      const response = await secureApi.post(`/api/v1/channex/properties/${propertyId}/create-room-types`);
      return response.data;
    } catch (error) {
      console.error(`Error creating room types in Channex:`, error);
      throw error;
    }
  },

  // Step 3: Create rate plan in Channex
  createRatePlanInChannex: async (propertyId) => {
    try {
      const response = await secureApi.post(`/api/v1/channex/properties/${propertyId}/create-rate-plan`);
      return response.data;
    } catch (error) {
      console.error(`Error creating rate plan in Channex:`, error);
      throw error;
    }
  },

  quickOnboard: async (propertyId) => {
    try {
      const response = await secureApi.post(`/api/properties/${propertyId}/quick-onboard`);
      return response.data;
    } catch (error) {
      console.error(`Error quick onboarding property:`, error);
      throw error;
    }
  },

  // Test Gathern iCal connection
  testGathernConnection: async (propertyId) => {
    try {
      const response = await secureApi.get(`/api/v1/properties/${propertyId}/gathern-test`);
      return response.data;
    } catch (error) {
      console.error(`Error testing Gathern connection:`, error);
      throw error;
    }
  },

  // Manual sync property with Gathern
  syncPropertyNow: async (propertyId) => {
    try {
      const response = await secureApi.post(`/api/v1/sync/manual`, { propertyId });
      return response.data;
    } catch (error) {
      console.error(`Error syncing property:`, error);
      throw error;
    }
  },

  // =====================================================
  // Ops Handoff - Intent Policies
  // =====================================================

  getIntentPolicies: async () => {
    try {
      const response = await secureApi.get('/api/v1/ops/intent-policies');
      return response.data;
    } catch (error) {
      console.error('Error fetching intent policies:', error);
      throw error;
    }
  },

  getIntentPolicy: async (intent) => {
    try {
      const response = await secureApi.get(`/api/v1/ops/intent-policies/${intent}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching intent policy for ${intent}:`, error);
      throw error;
    }
  },

  updateIntentPolicy: async (intent, data) => {
    try {
      const response = await secureApi.patch(`/api/v1/ops/intent-policies/${intent}`, data);
      return response.data;
    } catch (error) {
      console.error(`Error updating intent policy for ${intent}:`, error);
      throw error;
    }
  },

  // =====================================================
  // Ops Handoff - Live Conversations
  // =====================================================

  getLiveConversations: async (params = {}) => {
    try {
      const response = await secureApi.get('/api/v1/ops/conversations/live', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching live conversations:', error);
      throw error;
    }
  },

  getConversationDetails: async (conversationId) => {
    try {
      const response = await secureApi.get(`/api/v1/ops/conversations/${conversationId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching conversation ${conversationId}:`, error);
      throw error;
    }
  },

  joinConversation: async (conversationId) => {
    try {
      const response = await secureApi.post(`/api/v1/ops/conversations/${conversationId}/join`);
      return response.data;
    } catch (error) {
      console.error(`Error joining conversation ${conversationId}:`, error);
      throw error;
    }
  },

  resolveConversation: async (conversationId, resolutionNotes) => {
    try {
      const response = await secureApi.post(`/api/v1/ops/conversations/${conversationId}/resolve`, {
        resolutionNotes
      });
      return response.data;
    } catch (error) {
      console.error(`Error resolving conversation ${conversationId}:`, error);
      throw error;
    }
  },

  reassignConversation: async (conversationId, payload) => {
    try {
      const response = await secureApi.post(`/api/v1/ops/conversations/${conversationId}/reassign`, payload);
      return response.data;
    } catch (error) {
      console.error(`Error reassigning conversation ${conversationId}:`, error);
      throw error;
    }
  },

  sendOpsMessage: async (conversationId, message) => {
    try {
      const response = await secureApi.post(`/api/v1/ops/conversations/${conversationId}/send-message`, {
        message
      });
      return response.data;
    } catch (error) {
      console.error(`Error sending ops message to ${conversationId}:`, error);
      throw error;
    }
  }
};

export { secureApi, secureApiService };
export default secureApiService;
